import vidGbcvetHome from "../../assets/video/gbcvet-homepage-scroll.mp4.asset.json";
import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/gbcvet";

const img = (
  file: string,
  alt: string,
  width: number,
  height: number,
  span: 2 | 3 | 6 = 3,
  small = true,
) => ({
  src: `${dir}/${file}.webp`,
  ...(small ? { src900: `${dir}/${file}-900.webp` } : {}),
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

export const gbcvet: CaseStudy = {
  slug: "gbcvet",
  title: "Городская База Цветов",
  cardTitle: "Комплексное продвижение сети цветочных магазинов «Городская База Цветов»",
  subtitle: "Цветочная розница и доставка",
  industry: { label: "Розничные сети", slug: "retail" },
  eyebrow: "Комплексный маркетинг · Розничная сеть",
  h1: "Комплексное продвижение сети «Городская База Цветов»",
  lead:
    "Ведём digital-направление сети цветочных магазинов: интернет-магазин, RetailCRM, SEO, Яндекс Директ и продвижение точек в Картах. Онлайн-заказ, самовывоз и покупка в ближайшем магазине работают в одной системе.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить продвижение сети" },

  seo: {
    primaryKeyword: "продвижение сети цветочных магазинов",
    secondaryKeywords: [
      "комплексное продвижение цветочного магазина",
      "маркетинг сети цветочных магазинов",
      "SEO для интернет-магазина цветов",
      "Яндекс Директ для цветочного магазина",
      "геоперформанс для розничной сети",
      "продвижение сети магазинов в Яндекс Картах",
      "внедрение RetailCRM для цветочного магазина",
      "продвижение доставки цветов",
    ],
    semanticEntities: [
      "розничная сеть",
      "цветочный магазин",
      "интернет-магазин цветов",
      "доставка цветов",
      "самовывоз",
      "городские страницы",
      "RetailCRM",
      "программа лояльности",
      "Яндекс Карты",
      "геоперформанс",
      "Яндекс Директ",
      "SEO-структура",
      "веб-аналитика",
    ],
    excludedKeywords: [
      "заказать цветы",
      "доставка букетов",
      "купить розы",
      "цветы в Москве",
      "цветы с доставкой",
      "букет на день рождения",
    ],
    title: "Продвижение сети цветочных магазинов GBC — кейс HAY",
    description:
      "Кейс HAY.agency: сайт, RetailCRM, SEO, Яндекс Директ и геоперформанс для сети «Городская База Цветов».",
    h1: "Комплексное продвижение сети «Городская База Цветов»",
    slug: "gbcvet",
    canonicalUrl: `${SITE_URL}/cases/gbcvet`,
    ogTitle: "Комплексное продвижение сети «Городская База Цветов»",
    ogDescription:
      "Сайт, RetailCRM, SEO, Яндекс Директ и геоперформанс десятков магазинов сети в одной системе.",
    ogImage: `${SITE_URL}${dir}/gbcvet-og.webp`,
    robotsIndex: true,
    datePublished: "2023-04-01",
    dateModified: "2026-08-29",
  },

  links: {
    relatedService: {
      label: "Продвижение и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, городские разделы и сценарии получения заказа",
    },
    relatedIndustry: {
      label: "Геоперформанс: продвижение точек в Картах и Навигаторе",
      to: routes.geo,
      note: "Локальный спрос вокруг каждого магазина сети",
    },
    relatedCases: [
      {
        label: "Интернет-магазин и RetailCRM для сети «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Цветочный e-commerce: каталог, мобильная покупка и обработка заказов",
      },
      {
        label: "Комплексное продвижение строительной компании ESG",
        to: "/cases/esg",
        note: "Сайт, SEO и Директ для компании с несколькими направлениями",
      },
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "Крупный товарный каталог и заявки из поиска",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужно продвигать сеть магазинов в нескольких городах?",
    text: "Свяжем сайт, CRM, рекламу и локальное продвижение в одну систему привлечения покупателей.",
    button: "Обсудить продвижение сети",
  },

  client: {
    name: "Городская База Цветов",
    url: "https://gbcvet.ru/",
    city: "Москва и города Московской области",
    industry: "Цветочная розница и доставка",
  },
  year: "С 2023 года",
  services: [
    "Развитие интернет-магазина",
    "RetailCRM",
    "SEO-продвижение",
    "Яндекс Директ",
    "Геоперформанс",
    "Техническая поддержка",
  ],
  platform: "1С-Битрикс + RetailCRM",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "RetailCRM", to: routes.retailcrm },
    { label: "SEO", to: routes.seo },
    { label: "Яндекс Директ", to: routes.context },
    { label: "Геоперформанс", to: routes.geo },
  ],
  facts: [
    { label: "Клиент", value: "Городская База Цветов" },
    { label: "Отрасль", value: "Цветочная розница и доставка" },
    { label: "Формат", value: "Комплексное digital-сопровождение розничной сети" },
    { label: "Аудитория", value: "Онлайн-покупатели, гости магазинов, постоянные клиенты" },
  ],

  results: {
    title: "Что делаем в проекте",
    items: [
      "Развиваем интернет-магазин: каталог, городские разделы, доставка и самовывоз",
      "Поддерживаем работу RetailCRM: заказы, статусы, коммуникации, повторные продажи",
      "Ведём SEO по категориям, городам и информационным запросам",
      "Ведём кампании в Яндекс Директе: товарные, категорийные, сезонные",
      "Продвигаем магазины сети в Яндекс Картах и локальной рекламе",
      "Обеспечиваем техническую поддержку сайта и аналитику обращений",
    ],
  },

  story: [],
  features: [],

  design: {
    title: "Каналы ведут покупателя в удобный сценарий покупки",
    paragraphs: [
      "SEO развивает категории каталога, городские страницы и информационный спрос. Яндекс Директ закрывает товарные, категорийные, брендовые и сезонные запросы. Геоперформанс работает с людьми рядом с точкой: карточки магазинов в Картах, маршруты и локальная реклама.",
      "Каждый канал ведёт покупателя туда, где ему удобнее завершить покупку.",
    ],
    bullets: [
      "Поиск букета → SEO или Директ → карточка товара → доставка",
      "Поиск магазина рядом → Карты или геореклама → адрес и маршрут → покупка в магазине",
      "Повторная покупка → лояльность и коммуникации → каталог → новый заказ",
    ],
  },

  identity: { fonts: [], colors: [] },

  cover: img(
    "gbcvet-flower-retail-network-case-cover",
    "Комплексное продвижение сети цветочных магазинов «Городская База Цветов»: сайт, магазины сети и мобильные экраны",
    1920,
    1080,
    6,
  ),

  coverVideo: vidGbcvetHome.url,
  coverPoster: {
    src: `${dir}/gbcvet-homepage-scroll-poster.webp`,
    src900: `${dir}/gbcvet-homepage-scroll-poster-900.webp`,
    alt: "Главная страница интернет-магазина сети «Городская База Цветов»",
    width: 1920,
    height: 1080,
    span: 6,
    wide: true,
  },

  gallery: [],

  responsive: {
    text: [
      "Основная часть заказов приходит с телефона, поэтому мобильные сценарии — подбор букета, оформление заказа, поиск магазина и программа лояльности — прорабатываем отдельно.",
      "На смартфоне покупатель одинаково быстро доходит и до доставки, и до ближайшей точки самовывоза.",
    ],
    images: [
      {
        src: `${dir}/gbcvet-mobile-catalog.webp`,
        src900: `${dir}/gbcvet-mobile-catalog-900.webp`,
        alt: "Мобильные экраны интернет-магазина цветов: главная и каталог с фильтрами",
        width: 1920,
        height: 1920,
        span: 3,
      },
      {
        src: `${dir}/gbcvet-mobile-local.webp`,
        src900: `${dir}/gbcvet-mobile-local-900.webp`,
        alt: "Мобильные экраны: карта магазинов сети и программа лояльности",
        width: 1920,
        height: 1920,
        span: 3,
      },
    ],
  },

  columnBlocks: [
    {
      id: "system",
      title: "Сайт сети как точка входа во все каналы продаж",
      paragraphs: [
        "Покупатель выбирает город, находит ближайший магазин, заказывает доставку или оформляет самовывоз. За каждым действием стоит связка сайта, CRM, рекламы и локального продвижения, которую мы развиваем совместно с клиентом.",
      ],
      images: [
        img(
          "gbcvet-online-store-home",
          "Главная страница интернет-магазина сети «Городская База Цветов»",
          1920,
          1271,
          6,
        ),
      ],
      columns: [
        {
          title: "Интернет-магазин",
          items: ["Каталог и фильтры", "Городские разделы", "Доставка и самовывоз"],
        },
        
        { title: "Заказы", items: ["RetailCRM", "Статусы заказа", "Работа флористов"] },
        { title: "Привлечение", items: ["SEO", "Яндекс Директ", "Геоперформанс"] },
      ],
    },
    {
      id: "catalog",
      title: "Каталог под город, повод и параметры букета",
      paragraphs: [
        "Каталог строится по типам цветов, количеству, размеру, поводу и цене. Категорийные страницы вида «101 роза» закрывают частотные запросы и одновременно остаются рабочим инструментом выбора.",
      ],
      images: [
        img(
          "gbcvet-catalog-filters",
          "Каталог интернет-магазина цветов с фильтрами по типу, количеству и цене",
          1920,
          1246,
          3,
        ),
        img(
          "gbcvet-catalog-category",
          "Категорийная страница «101 роза» с фильтрами и подборкой букетов",
          1920,
          1246,
          3,
        ),
      ],
      imageCols: 2,
      caption: "Фильтры каталога и SEO-структура категорий развиваются как единая система.",
    },
    {
      id: "promo",
      title: "Промо-страницы для сезонных предложений",
      paragraphs: [
        "Для рекламных кампаний разработали отдельные посадочные страницы, которые объединяют условия акции, преимущества сервиса и подборку товаров со сниженными ценами.",
      ],
      images: [
        img(
          "gbcvet-sale-promo",
          "Промо-страница акции: условия скидки и подборка букетов со сниженными ценами",
          1920,
          1246,
          6,
        ),
      ],
    },
    {
      id: "local",
      title: "Города и магазины: локальный слой сети",
      paragraphs: [
        "Городские страницы доставки и раздел магазинов с картой, адресами и рейтингами помогают покупателю выбрать удобный формат получения заказа. Эти же данные используем в геопродвижении точек.",
      ],
      images: [
        img("gbcvet-store-network", "Магазины сети на карте: адреса, метро и рейтинги", 1920, 1246, 3),
        img("gbcvet-city-landing", "Городская страница доставки цветов", 1920, 1246, 3),
      ],
      imageCols: 2,
    },
    {
      id: "retailcrm",
      title: "RetailCRM и удержание покупателей",
      paragraphs: [
        "Заказы и клиентская история собираются в RetailCRM: обращения из разных городов и каналов, статусы, распределение заказов по магазинам. Программа лояльности на сайте поддерживает повторные покупки.",
      ],
      columns: [
        {
          title: "Маршрут заказа",
          items: ["Реклама или поиск", "Сайт", "RetailCRM", "Магазин и флорист", "Выдача заказа"],
        },
        {
          title: "Что настроено",
          items: ["Приём заказов", "Карточка клиента", "Статусы", "Распределение по точкам"],
        },
        {
          title: "Работа с клиентом",
          items: ["Коммуникации", "Программа лояльности", "Повторные продажи", "Аналитика"],
        },
      ],
      images: [
        img(
          "gbcvet-loyalty-program",
          "Программа лояльности сети: условия участия и начисление бонусов",
          1920,
          1246,
          6,
        ),
      ],
    },
  ],

  scale: {
    title: "Масштаб системы",
    paragraphs: [
      "Показатели описывают текущий охват проекта: города доставки, розничную сеть и направления работ, которые ведём одновременно.",
    ],
    metrics: [
      { value: "11", label: "городов в выборе доставки на сайте" },
      { value: "60+", label: "магазинов сети с самовывозом" },
      { value: "6", label: "направлений: сайт, CRM, SEO, Директ, гео, поддержка" },
    ],
    note: "Данные отражают текущий масштаб проекта и могут обновляться.",
  },

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      {
        label: "Развитие интернет-магазина",
        to: routes.ecom,
        note: "Каталог, городские разделы, доставка и самовывоз",
      },
      {
        label: "RetailCRM",
        to: routes.retailcrm,
        note: "Заказы, статусы, коммуникации и повторные продажи",
      },
      {
        label: "SEO-продвижение",
        to: routes.seo,
        note: "Категории, города и информационные страницы",
      },
      {
        label: "Яндекс Директ",
        to: routes.context,
        note: "Товарные, категорийные, брендовые и сезонные кампании",
      },
      {
        label: "Геоперформанс",
        to: routes.geo,
        note: "Карты, Навигатор и локальная реклама вокруг магазинов",
      },
      {
        label: "Техническая поддержка и аналитика",
        to: routes.uslugi,
        note: "Стабильность сайта и отчётность по обращениям",
      },
    ],
  },
};
